/**
 * RunSurvey Page
 *
 * Public page for respondents to fill out surveys.
 * Supports multilingual surveys with RTL/LTR text direction.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import FormRenderer from './components/FormRenderer'
import { useAuth } from './auth-context'
import * as surveysApi from './api/surveys'
import * as responsesApi from './api/responses'
import './App.css'

// Language display names for the selector
const LANGUAGE_NAMES = {
  en: 'English',
  hi: 'हिन्दी',
  ta: 'தமிழ்',
  te: 'తెలుగు',
  kn: 'ಕನ್ನಡ',
  ml: 'മലയാളം',
  mr: 'मराठी',
  gu: 'ગુજરાતી',
  bn: 'বাংলা',
  pa: 'ਪੰਜਾਬੀ',
  ar: 'العربية',
  ur: 'اردو',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  pt: 'Português',
  zh: '中文',
  ja: '日本語',
  ko: '한국어',
}

// Hook to fetch survey data
function useSurveyData(id) {
  const [state, setState] = useState({ loading: true, data: null, error: null })

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await surveysApi.getSurvey(id)
        if (!cancelled) setState({ loading: false, data, error: null })
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, error: e })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  return state
}

// Language selector component for multilingual surveys
function LanguageSelector({ languages, currentLanguage, onChange }) {
  if (!languages || languages.length <= 1) return null

  return (
    <div className="language-selector">
      <label htmlFor="lang-select">Language: </label>
      <select
        id="lang-select"
        value={currentLanguage}
        onChange={(e) => onChange(e.target.value)}
        className="lang-select">
        {languages.map((lang) => (
          <option key={lang} value={lang}>
            {LANGUAGE_NAMES[lang] || lang}
          </option>
        ))}
      </select>
    </div>
  )
}

export default function RunSurvey() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const isLoggedIn = !!user
  const fieldAgentToken = searchParams.get('token') || null
  const { loading, data, error } = useSurveyData(id)
  const [submitting, setSubmitting] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  const [languageSource, setLanguageSource] = useState('browser')
  const startTimeRef = useRef(Date.now())
  const metadataRef = useRef(null)
  const responseIdRef = useRef(null)

  // Get language from URL parameter
  const urlLang = searchParams.get('lang') || searchParams.get('language')

  // Determine the active language based on priority: user selection > URL param > survey default > browser
  const activeLanguage =
    selectedLanguage || urlLang || data?.default_language || responsesApi.getLanguage()

  // Handle language change by user
  const handleLanguageChange = useCallback((lang) => {
    setSelectedLanguage(lang)
    setLanguageSource('user_choice')
  }, [])

  // Set initial language source based on URL param
  useEffect(() => {
    if (urlLang) {
      setLanguageSource('url_param')
    }
  }, [urlLang])

  // Apply RTL/LTR direction and language when survey loads
  useEffect(() => {
    if (data) {
      // Set document direction based on survey's text_direction
      const direction = data.text_direction || 'ltr'
      document.documentElement.dir = direction
      document.documentElement.lang = activeLanguage

      // Cleanup on unmount
      return () => {
        document.documentElement.dir = 'ltr'
        document.documentElement.lang = ''
      }
    }
  }, [data, activeLanguage])

  // Capture metadata when component mounts
  useEffect(() => {
    const captureMetadata = async () => {
      try {
        // Location remains opt-in until the explicit consent flow is implemented.
        metadataRef.current = await responsesApi.captureResponseMetadata({
          includeGeolocation: false,
        })
      } catch {
        metadataRef.current = {
          geolocation: null,
          timezone: responsesApi.getTimezone(),
          language: responsesApi.getLanguage(),
          clientInfo: responsesApi.captureClientInfo(),
        }
      }
    }

    captureMetadata()
    startTimeRef.current = Date.now()
  }, [])

  // Handle form submission
  const handleSubmit = async (answers) => {
    if (!data?.id) return

    setSubmitting(true)
    try {
      // Calculate duration
      const durationMs = Date.now() - startTimeRef.current

      // Get metadata
      const metadata = metadataRef.current || {
        geolocation: null,
        timezone: responsesApi.getTimezone(),
        language: responsesApi.getLanguage(),
        clientInfo: responsesApi.captureClientInfo(),
      }

      // Submit response with all metadata including language source
      await responsesApi.submitResponse(data.id, {
        answers,
        durationMs,
        geolocation: metadata.geolocation,
        timezone: metadata.timezone,
        language: activeLanguage,
        languageSource: languageSource,
        clientInfo: metadata.clientInfo,
        meta: {
          survey_title: data.title,
          survey_version: data.version,
        },
        fieldAgentToken,
      })

      console.log('Survey response submitted successfully')
    } catch (e) {
      console.error('Failed to submit survey response:', e)
      throw e // Re-throw so FormRenderer can handle it
    } finally {
      setSubmitting(false)
    }
  }

  // Get form definition from survey data
  const form = data?.definition || null
  // Read settings from survey level OR from definition (FormBuilder stores them in definition)
  const textDirection = data?.text_direction || form?.text_direction || 'ltr'
  const availableLanguages = data?.available_languages || form?.available_languages || []
  const allowedResponseTypes = data?.allowed_response_types ||
    form?.allowed_response_types || ['text']
  // Brand image: survey-specific theme overrides organization logo
  const brandImageUrl = data?.theme?.brand_image_url || data?.organization_logo_url || null

  return (
    <div className="run-survey-page" dir={textDirection}>
      <div className="container" style={{ padding: '1rem' }}>
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading survey...</p>
          </div>
        )}
        {error && (
          <div className="error-state" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <div
              style={{
                width: 64,
                height: 64,
                margin: '0 auto 1.25rem',
                borderRadius: '50%',
                background: '#fff5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.75rem',
              }}>
              ⚠
            </div>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2d3748', fontSize: '1.5rem' }}>
              {error.status === 404 ? 'Survey Not Available' : 'Unable to Load Survey'}
            </h2>
            <p
              style={{ color: '#718096', maxWidth: 420, margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
              {error.status === 404
                ? 'This survey may have been removed, is not yet published, or the link may be incorrect.'
                : error.message ||
                  'Something went wrong while loading the survey. Please try again later.'}
            </p>
            {isLoggedIn && (
              <Link
                to="/"
                style={{
                  display: 'inline-block',
                  padding: '0.75rem 1.5rem',
                  background: '#0D9488',
                  color: 'white',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}>
                Back to Home
              </Link>
            )}
          </div>
        )}
        {form && (
          <div className="page-section survey-runner-section">
            {/* Language selector for multilingual surveys */}
            <LanguageSelector
              languages={availableLanguages}
              currentLanguage={activeLanguage}
              onChange={handleLanguageChange}
            />
            <FormRenderer
              form={form}
              onSubmit={handleSubmit}
              submitting={submitting}
              surveyId={data?.id}
              responseId={responseIdRef.current}
              uploadToken={data?.upload_token}
              textDirection={textDirection}
              allowedResponseTypes={allowedResponseTypes}
              brandImageUrl={brandImageUrl}
            />
          </div>
        )}
        {!loading && !error && !form && (
          <div className="error-state" style={{ textAlign: 'center', padding: '3rem 1.5rem' }}>
            <h2 style={{ margin: '0 0 0.5rem 0', color: '#2d3748', fontSize: '1.5rem' }}>
              Survey Not Available
            </h2>
            <p
              style={{ color: '#718096', maxWidth: 420, margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
              This survey could not be loaded. It may not have any questions yet or the link may be
              incorrect.
            </p>
            {isLoggedIn && (
              <Link
                to="/"
                style={{
                  display: 'inline-block',
                  padding: '0.75rem 1.5rem',
                  background: '#0D9488',
                  color: 'white',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: 600,
                }}>
                Back to Home
              </Link>
            )}
          </div>
        )}
        {isLoggedIn && (
          <p style={{ marginTop: '0.75rem', textAlign: 'center' }}>
            <Link to="/">Back to Home</Link>
          </p>
        )}
      </div>
    </div>
  )
}
